/**
 * Elektron xizmatlar ma'lumotnomasi (ЧТЗ 4.2.9, 4.2.11.5).
 *
 * Har bir xizmat uchun: kartochka (nom, vazifa, aniq tugmalar) va «Batafsil»
 * sahifasi (kim foydalanadi, ko'rsatuvchi tashkilot va tizim, kerakli
 * ma'lumotlar, bosqichlar, natija, muddat va to'lov, yordam).
 *
 * Muddat, narx va hujjatlar ro'yxati mas'ul tizimdan olinadi yoki tasdiqlangan
 * ma'lumotnoma asosida yuritiladi — shu sababli bu yerda aniq raqamlar
 * yozilmaydi: matn qaysi tizimda ko'rsatilishini aytadi. Tasdiqlangan
 * qiymatlar kelganda `terms` maydonlari to'ldiriladi (`source` — manba).
 *
 * `gateway` — `ServiceLink.code` (backend shlyuzi, `apps/portal/seeds.py`).
 * `mapLayers` — «Mos maydonlarni xaritada ko'rish» ochadigan ochiq qatlamlar.
 */
import type { IconName } from "@/shared/ui/Icon";

export type ServiceActionKind = "gateway" | "map" | "link";

export interface ServiceAction {
  kind: ServiceActionKind;
  label: string;
  icon: IconName;
  /** `gateway` — ServiceLink kodi; `link` — ichki marshrut. */
  target?: string;
  /** Tugma yonidagi izoh (masalan «OneID talab qilinadi»). */
  note?: string;
}

export interface ServicePurpose {
  code: string;
  title: string;
  text: string;
}

export interface ServiceTerm {
  label: string;
  value: string;
  /** Qiymat manbai: mas'ul tizim yoki tasdiqlangan ma'lumotnoma. */
  source: string;
}

export interface CatalogService {
  code: string;
  group: "state" | "platform";
  icon: IconName;
  title: string;
  /** Kartochkadagi qisqa vazifa (bir-ikki jumla). */
  summary: string;
  /** Kartochka va sahifadagi asosiy amallar (birinchisi — asosiy tugma). */
  actions: ServiceAction[];
  /** «Qanday ishlaydi» — kartochkada bir qator. */
  howItWorks: string;
  /** Kirish talabi kartochkada ko'rinadi. */
  access: string;
  /** Xizmat turlari / maqsadlar (tanlanadi, xaritaga uzatiladi). */
  purposesTitle?: string;
  purposes?: ServicePurpose[];
  mapLayers?: string[];
  /** Xaritada maydon tanlanganda uchastka raqami sifatida ko'rsatiladigan atribut. */
  plotField?: string;
  /** Xaritadan xizmatga o'tish (uchastka kartochkasidagi tugma). */
  plotAction?: ServiceAction;
  audience: string[];
  provider: { organization: string; system: string; url?: string };
  requirements: string[];
  steps: string[];
  result: string;
  terms: ServiceTerm[];
  help: Array<{ label: string; to: string }>;
}

const HELP = [
  { label: "Yo'riqnomalar va video darslar", to: "/haqida#yoriqnomalar" },
  { label: "Ko'p beriladigan savollar", to: "/#savol-javob" },
  { label: "Aloqa va ishonch telefoni", to: "/aloqa" },
];

export const SERVICE_CATALOG: CatalogService[] = [
  {
    code: "ruxsatnoma",
    group: "state",
    icon: "file",
    title: "O'rmon fondidan ruxsatnoma asosida foydalanish",
    summary:
      "Chorva boqish, pichan o'rish, asalari uyalarini joylashtirish va madaniy tadbirlar uchun ruxsatnoma.",
    howItWorks:
      "Maqsadni tanlaysiz → ochiq xaritada mos maydonni ko'rasiz → Ruxsatnoma-urmon.uz xizmatida ariza berasiz.",
    access: "OneID orqali",
    actions: [
      { kind: "gateway", target: "ruxsatnoma", label: "Ruxsatnoma olish", icon: "file", note: "Ruxsatnoma-urmon.uz" },
      { kind: "map", label: "Mos maydonlarni xaritada ko'rish", icon: "map" },
    ],
    purposesTitle: "Foydalanish maqsadi",
    purposes: [
      { code: "yaylov", title: "Chorva boqish", text: "O'rmon fondi yaylovlarida mol, qo'y-echki boqish." },
      { code: "pichanzor", title: "Pichan o'rish", text: "Pichanzorlarda o'rim va pichan tayyorlash." },
      { code: "asalari", title: "Asalari uyalarini joylashtirish", text: "Asalarichilik uchun uyalarni vaqtincha joylashtirish." },
      { code: "tadbir", title: "Madaniy tadbirlar o'tkazish", text: "Madaniy-ma'rifiy, sport va dam olish tadbirlari." },
    ],
    mapLayers: ["forest_fund"],
    plotField: "name",
    plotAction: { kind: "gateway", target: "ruxsatnoma", label: "Shu maydon uchun ruxsatnoma olish", icon: "file" },
    audience: [
      "Jismoniy shaxslar (fermer, chorvador, asalarichi)",
      "Yakka tartibdagi tadbirkorlar va yuridik shaxslar",
      "Tadbir tashkilotchilari",
    ],
    provider: {
      organization: "Hududiy o'rmon xo'jaligi (Agentlik tizimi)",
      system: "Ruxsatnoma-urmon.uz",
      url: "https://ruxsatnoma-urmon.uz",
    },
    requirements: [
      "OneID orqali tasdiqlangan shaxs (yuridik shaxs uchun — STIR)",
      "Foydalanish maqsadi va muddati",
      "Maydon: o'rmon xo'jaligi, kvartal yoki xaritadagi uchastka",
      "Maqsadga oid ma'lumot: bosh soni, uyalar soni yoki tadbir dasturi",
    ],
    steps: [
      "Foydalanish maqsadini tanlang",
      "Ochiq xaritada mos maydonni ko'ring va uchastka raqamini belgilang",
      "«Ruxsatnoma olish» — OneID orqali Ruxsatnoma-urmon.uz ga o'tasiz",
      "Arizani to'ldirib yuborasiz, o'rmon xo'jaligi ko'rib chiqadi",
      "To'lovni amalga oshirasiz va ruxsatnomani olasiz",
    ],
    result: "Elektron ruxsatnoma (maqsad, maydon va muddat ko'rsatilgan holda)",
    terms: [
      { label: "Ko'rib chiqish muddati", value: "Ariza berishda Ruxsatnoma-urmon.uz tizimida ko'rsatiladi", source: "Ruxsatnoma-urmon.uz" },
      { label: "To'lov", value: "Maqsad va maydonga qarab tizim hisoblaydi; ariza tasdiqlangach to'lanadi", source: "Ruxsatnoma-urmon.uz" },
    ],
    help: HELP,
  },
  {
    code: "osimliklar",
    group: "state",
    icon: "leaf",
    title: "Dorivor va yovvoyi o'simliklarni terish",
    summary:
      "Dorivor o'simliklar va boshqa o'simlik resurslarini yig'ish uchun ruxsat yoki litsenziya.",
    howItWorks:
      "O'simlik turi va maydon tanlanadi → tegishli ruxsatnoma (licenses.uz) yoki auksion (E-AUKSION) xizmatiga yo'naltirilasiz.",
    access: "OneID orqali",
    actions: [
      { kind: "gateway", target: "licenses", label: "Ruxsat olish", icon: "file", note: "licenses.uz" },
      { kind: "map", label: "Mos maydonlarni xaritada ko'rish", icon: "map" },
      { kind: "gateway", target: "eauksion", label: "Auksionga o'tish", icon: "scale", note: "E-AUKSION" },
    ],
    purposesTitle: "O'simlik resursi turi",
    purposes: [
      { code: "dorivor", title: "Dorivor o'simliklar", text: "Dorivor o'tlar, ildizlar va mevalarni tayyorlash." },
      { code: "yovvoyi", title: "Yovvoyi meva va yong'oqlar", text: "Yovvoyi mevalar, yong'oq va pista yig'ish." },
      { code: "boshqa", title: "Boshqa o'simlik resurslari", text: "Bezak, oziq-ovqat va xo'jalik uchun o'simliklar." },
    ],
    mapLayers: ["forest_fund", "auction_plots"],
    plotField: "name",
    plotAction: { kind: "gateway", target: "licenses", label: "Shu maydon uchun ruxsat olish", icon: "file" },
    audience: [
      "Yuridik shaxslar va yakka tartibdagi tadbirkorlar (tayyorlovchilar)",
      "Farmatsevtika va qayta ishlash korxonalari",
    ],
    provider: {
      organization: "Vakolatli ruxsat beruvchi organ; maydon bo'yicha — hududiy o'rmon xo'jaligi",
      system: "licenses.uz (savdoga qo'yilgan maydonlar — E-AUKSION)",
      url: "https://licenses.uz",
    },
    requirements: [
      "OneID orqali tasdiqlangan shaxs, STIR",
      "O'simlik turi va tayyorlanadigan hajm",
      "Maydon: o'rmon xo'jaligi yoki xaritadagi uchastka",
      "Yig'ish muddati",
    ],
    steps: [
      "O'simlik turini tanlang",
      "Xaritada mos maydonni ko'ring",
      "Maydon savdoga qo'yilgan bo'lsa — E-AUKSION dagi savdoga o'ting",
      "Aks holda — licenses.uz da ariza bering (OneID orqali)",
      "Ruxsat yoki litsenziya olinadi, yig'ish hajmi hisobga olinadi",
    ],
    result: "Ruxsatnoma yoki litsenziya (o'simlik turi, hajm, maydon va muddat bilan)",
    terms: [
      { label: "Ko'rib chiqish muddati", value: "licenses.uz da ariza berishda ko'rsatiladi", source: "licenses.uz" },
      { label: "To'lov", value: "Davlat boji va resurs uchun to'lov tizim tomonidan hisoblanadi; auksionda — savdo natijasi", source: "licenses.uz / E-AUKSION" },
    ],
    help: HELP,
  },
  {
    code: "ijara",
    group: "state",
    icon: "polygon",
    title: "O'rmon fondi yerlarini ijaraga olish",
    summary: "Ijaraga qo'yilgan uchastkalarni xaritada ko'rish va E-AUKSION dagi savdoda ishtirok etish.",
    howItWorks:
      "Maydon ochiq xaritada ko'riladi → lot kartochkasidan E-AUKSION dagi tegishli savdoga o'tiladi.",
    access: "Ko'rish — ochiq; auksion — OneID orqali",
    actions: [
      { kind: "map", label: "Maydonlarni ko'rish", icon: "map", note: "Ro'yxatdan o'tmasdan" },
      { kind: "gateway", target: "eauksion", label: "Auksionda ishtirok etish", icon: "scale", note: "E-AUKSION" },
    ],
    mapLayers: ["auction_plots", "lease_plots"],
    plotField: "auction_number",
    plotAction: { kind: "gateway", target: "eauksion", label: "Shu lot savdosiga o'tish", icon: "scale" },
    audience: [
      "Yuridik shaxslar va yakka tartibdagi tadbirkorlar",
      "Fermer xo'jaliklari",
    ],
    provider: {
      organization: "Hududiy o'rmon xo'jaligi (lotni savdoga qo'yuvchi)",
      system: "E-AUKSION elektron savdo maydonchasi",
      url: "https://e-auksion.uz",
    },
    requirements: [
      "OneID orqali tasdiqlangan shaxs, STIR",
      "E-AUKSION da ro'yxatdan o'tish va zakalat puli",
      "Lot raqami (xaritadagi uchastka kartochkasida)",
    ],
    steps: [
      "«Maydonlarni ko'rish» — ochiq xaritada auksion yerlarini ko'ring",
      "Uchastka kartochkasida lot raqami va maydonini tekshiring",
      "«Auksionda ishtirok etish» — E-AUKSION dagi savdoga o'ting",
      "Savdoda g'olib bo'lsangiz, ijara shartnomasi tuziladi",
      "Shartnoma va to'lovlar «Ijara hisobi» xizmatida ko'rinadi",
    ],
    result: "Savdo bayonnomasi va ijara shartnomasi",
    terms: [
      { label: "Savdo muddati", value: "Har bir lot uchun E-AUKSION e'lonida ko'rsatiladi", source: "E-AUKSION" },
      { label: "To'lov", value: "Boshlang'ich narx, zakalat va ijara haqi lot shartlarida belgilanadi", source: "E-AUKSION" },
    ],
    help: HELP,
  },
  {
    code: "billing",
    group: "platform",
    icon: "briefcase",
    title: "Ijara hisobi va to'lovlar",
    summary:
      "Ijara shartnomasi, hisoblangan to'lov, to'lov muddati, qarzdorlik, penya va to'lov tarixi.",
    howItWorks: "OneID orqali kirgandan keyin faqat sizga tegishli shartnoma va to'lovlar ochiladi.",
    access: "OneID orqali",
    actions: [{ kind: "gateway", target: "billing", label: "Ijara hisobini ko'rish", icon: "briefcase" }],
    audience: ["O'rmon fondi yerlari ijarachilari (jismoniy va yuridik shaxslar)"],
    provider: {
      organization: "O'rmon va yashil hududlarni ko'paytirish, cho'llanishga qarshi kurashish agentligi",
      system: "Smart Forestry — «Ijara hisobi» (Billing-urmon)",
    },
    requirements: ["OneID orqali kirish (shartnomadagi JShShIR yoki STIR bo'yicha)"],
    steps: [
      "«Ijara hisobini ko'rish» — OneID orqali kirasiz",
      "Shartnomalar, hisob-fakturalar va to'lov muddatlarini ko'rasiz",
      "Qarzdorlik va penya bo'lsa — miqdori va sababi ko'rsatiladi",
      "To'lov tarixini ko'rasiz va hisob-fakturani yuklab olasiz",
    ],
    result: "Hisob-kitob holati, hisob-faktura va to'lovlar tarixi",
    terms: [
      { label: "To'lov muddati", value: "Ijara shartnomasidagi to'lov grafigi bo'yicha", source: "Ijara shartnomasi" },
      { label: "Penya", value: "Kechiktirilgan to'lov uchun shartnoma shartlariga muvofiq hisoblanadi", source: "Ijara shartnomasi" },
    ],
    help: HELP,
  },
  {
    code: "market",
    group: "platform",
    icon: "store",
    title: "«Elektron yashil olam»",
    summary: "Ko'chat va o'rmon mahsulotlari katalogi, buyurtma va yetkazib berish.",
    howItWorks:
      "Katalog ro'yxatdan o'tmasdan ochiladi. «Sotib olish» bosilganda OneID so'raladi; kirgandan keyin tanlangan mahsulot bilan xarid davom etadi.",
    access: "Katalog — ochiq; xarid — OneID orqali",
    actions: [
      { kind: "link", target: "/yashil-olam", label: "Katalogni ko'rish", icon: "store", note: "Ro'yxatdan o'tmasdan" },
      { kind: "gateway", target: "market", label: "Xarid qilish", icon: "cart", note: "OneID orqali" },
    ],
    audience: ["Fuqarolar, fermer xo'jaliklari, tashkilotlar"],
    provider: {
      organization: "Ko'chatxonalar va o'rmon xo'jaliklari (sotuvchilar)",
      system: "Smart Forestry — «Elektron yashil olam»",
    },
    requirements: ["Katalogni ko'rish uchun — hech narsa", "Xarid uchun — OneID, yetkazib berish manzili"],
    steps: [
      "Katalogdan mahsulotni tanlang (ro'yxatdan o'tmasdan)",
      "«Sotib olish» — OneID orqali kirasiz",
      "Kirgandan keyin tanlangan mahsulot savatga qo'shilgan holda xarid davom etadi",
      "Buyurtmani rasmiylashtirasiz va to'laysiz",
      "Buyurtma holati kabinetda kuzatiladi",
    ],
    result: "Buyurtma, to'lov cheki va yetkazib berish",
    terms: [
      { label: "Narx va yetkazish", value: "Har bir mahsulot kartochkasida sotuvchi tomonidan ko'rsatiladi", source: "Sotuvchi" },
    ],
    help: HELP,
  },
];

export function findService(code: string): CatalogService | undefined {
  return SERVICE_CATALOG.find((item) => item.code === code);
}

/** Xizmatning ochiq xaritadagi manzili (qatlam va maqsad filtri bilan). */
export function serviceMapHref(service: CatalogService, purpose = ""): string {
  const params = new URLSearchParams({ xizmat: service.code });
  if (purpose) params.set("maqsad", purpose);
  return `/xarita?${params.toString()}`;
}
